// Copyright (c) 2026 Riddle, LLC. All rights reserved.
// Author: Ray Harris

import {
  makeStyles,
  tokens,
  Title2,
  Caption1,
  Avatar,
  Button,
  Hamburger,
  Menu,
  MenuTrigger,
  MenuPopover,
  MenuList,
  MenuItem,
  MenuDivider,
} from "@fluentui/react-components";
import { navigateTo } from "../../router/navigation";
import { useAuthSession } from "../../hooks/useAuthSession";
import { useTenantBranding } from "../../hooks/useTenantBranding";
import { TutorialToggle } from "../../features/tutorial/components/TutorialToggle";
import { COMPACT_VIEWPORT_QUERY } from "../../hooks/useMediaQuery";

const TAGLINE_HIDDEN_QUERY = "(max-width: 600px)";

const useStyles = makeStyles({
  root: {
    position: "sticky",
    top: "0",
    zIndex: 10,
    borderRadius: tokens.borderRadiusXLarge,
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    backgroundColor: "var(--riddle-surface-secondary)",
    backdropFilter: "blur(10px)",
    WebkitBackdropFilter: "blur(10px)",
    boxShadow: tokens.shadow8,
    padding: `${tokens.spacingVerticalM} ${tokens.spacingHorizontalL}`,
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: tokens.spacingHorizontalM,
    minWidth: 0,
    maxWidth: "100%",
    [`@media ${COMPACT_VIEWPORT_QUERY}`]: {
      padding: `${tokens.spacingVerticalS} ${tokens.spacingHorizontalM}`,
      gap: tokens.spacingHorizontalS,
    },
  },
  brandGroup: {
    display: "flex",
    alignItems: "center",
    gap: tokens.spacingHorizontalS,
    minWidth: 0,
  },
  menuToggle: {
    display: "none",
    flexShrink: "0",
    "@media (max-width: 980px)": {
      display: "inline-flex",
    },
  },
  brand: {
    display: "flex",
    alignItems: "center",
    gap: tokens.spacingHorizontalM,
    flexShrink: "1",
    minWidth: 0,
  },
  mark: {
    width: "32px",
    height: "32px",
    borderRadius: tokens.borderRadiusMedium,
    backgroundColor: tokens.colorBrandBackground,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    color: tokens.colorNeutralForegroundInverted,
    fontWeight: tokens.fontWeightBold,
    fontSize: tokens.fontSizeBase400,
    flexShrink: "0",
    userSelect: "none",
  },
  markImage: {
    width: "32px",
    height: "32px",
    borderRadius: tokens.borderRadiusMedium,
    objectFit: "cover",
    flexShrink: "0",
    display: "block",
  },
  titleGroup: {
    display: "grid",
    gap: "2px",
    minWidth: 0,
  },
  title: {
    color: tokens.colorNeutralForeground1,
    lineHeight: "1",
  },
  subtitle: {
    color: tokens.colorNeutralForeground3,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    minWidth: 0,
    [`@media ${TAGLINE_HIDDEN_QUERY}`]: {
      display: "none",
    },
  },
  actions: {
    display: "flex",
    alignItems: "center",
    gap: tokens.spacingHorizontalS,
    flexShrink: "0",
  },
  settingsButton: {},
  avatarButton: {
    minWidth: "32px",
    minHeight: "32px",
    padding: tokens.spacingVerticalXXS,
    borderRadius: tokens.borderRadiusCircular,
  },
  unauthLabel: {
    color: tokens.colorNeutralForeground3,
    fontSize: tokens.fontSizeBase200,
  },
  menuHeader: {
    padding: `${tokens.spacingVerticalXS} ${tokens.spacingHorizontalM}`,
    paddingBottom: tokens.spacingVerticalXS,
  },
  menuHeaderText: {
    color: tokens.colorNeutralForeground1,
    fontSize: tokens.fontSizeBase300,
    fontWeight: tokens.fontWeightSemibold,
    display: "block",
    maxWidth: "200px",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  menuHeaderWorkspace: {
    color: tokens.colorNeutralForeground3,
    fontSize: tokens.fontSizeBase200,
    display: "block",
    maxWidth: "200px",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
});

export function getUserInitials(userLabel: string): string {
  const base = userLabel.split("@")[0] ?? "";
  const initials = base
    .split(/[\s.\-_]+/)
    .filter((part) => part.length > 0)
    .map((part) => part[0]!.toUpperCase())
    .slice(0, 2)
    .join("");
  return initials.length > 0 ? initials : "U";
}

interface TopBannerProps {
  isAuthenticated: boolean;
  userLabel: string;
  onSignOut?: () => void;
  onMenuButtonClick?: (() => void) | undefined;
}

export function TopBanner({
  isAuthenticated,
  userLabel,
  onSignOut,
  onMenuButtonClick,
}: TopBannerProps) {
  const styles = useStyles();
  const { logout } = useAuthSession();
  const { businessName } = useTenantBranding();

  const userInitials = isAuthenticated ? getUserInitials(userLabel) : "?";

  function handleSignOut() {
    if (onSignOut) {
      onSignOut();
      navigateTo("/");
    } else {
      navigateTo("/");
      void logout();
    }
  }

  return (
    <header className={styles.root}>
      <div className={styles.brandGroup}>
        {onMenuButtonClick && (
          <Hamburger
            className={styles.menuToggle}
            onClick={onMenuButtonClick}
            aria-label="Toggle navigation menu"
          />
        )}
        <div className={styles.brand}>
          <img
            src="/riddle-brand-icon.png"
            alt="Riddle"
            className={styles.markImage}
          />
          <div className={styles.titleGroup}>
            <Title2 className={styles.title}>Riddle</Title2>
            <Caption1 className={styles.subtitle}>
              CRM · Communications · Transactions · Property
            </Caption1>
          </div>
        </div>
      </div>

      <div className={styles.actions}>
        {isAuthenticated ? (
          <>
            <span data-tour-step="world-tour-done">
              <TutorialToggle compactIconOnly />
            </span>
            <Menu>
              <MenuTrigger disableButtonEnhancement>
                <Button
                  className={styles.avatarButton}
                  appearance="subtle"
                  aria-label={`Profile menu for ${userLabel}`}
                >
                  <Avatar
                    name={userLabel}
                    initials={userInitials}
                    size={28}
                    color="brand"
                    aria-hidden="true"
                  />
                </Button>
              </MenuTrigger>

              <MenuPopover>
                <MenuList>
                  <div className={styles.menuHeader}>
                    <span className={styles.menuHeaderText}>{userLabel}</span>
                    {businessName ? (
                      <span className={styles.menuHeaderWorkspace}>
                        {businessName}
                      </span>
                    ) : null}
                  </div>
                  <MenuDivider />
                  <MenuItem onClick={() => navigateTo("/settings")}>
                    Settings
                  </MenuItem>
                  <MenuItem onClick={() => navigateTo("/settings?tab=profile")}>
                    Profile
                  </MenuItem>
                  <MenuDivider />
                  <MenuItem onClick={() => void handleSignOut()}>
                    Sign Out
                  </MenuItem>
                </MenuList>
              </MenuPopover>
            </Menu>
          </>
        ) : (
          <span className={styles.unauthLabel}>Authentication Required</span>
        )}
      </div>
    </header>
  );
}
